import { Component, ElementRef, ChangeDetectorRef, inject, signal } from '@angular/core';
import { ThemeService } from '@verticalbridge/ui-components';

interface ThemeOption {
  id: string;
  name: string;
  icon: IconKey;
}

type IconKey =
  | 'columns'
  | 'certificate'
  | 'sun'
  | 'moon'
  | 'chat'
  | 'snowflake'
  | 'tv'
  | 'heart'
  | 'diamond'
  | 'droplet'
  | 'leaf';

// Lucide-style SVG path data for each theme icon. Lets us avoid the FontAwesome
// dependency OC has, while keeping a recognisable per-theme glyph.
export const THEME_ICON_PATHS: Record<IconKey, string> = {
  columns: 'M3 3h7v18H3zM14 3h7v18h-7z',
  certificate: 'M12 15l-3.5 3.5L7 17l5-5 5 5-1.5 1.5L12 15zm0-3a4 4 0 100-8 4 4 0 000 8zm-7 2h3v8l-3-2-3 2v-8zM16 14h3v8l-3-2',
  sun: 'M12 4V2m0 20v-2m8-8h2M2 12h2m13.66-5.66l1.41-1.41M4.93 19.07l1.41-1.41m12.73 1.41l-1.41-1.41M4.93 4.93l1.41 1.41M16 12a4 4 0 11-8 0 4 4 0 018 0z',
  moon: 'M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z',
  chat: 'M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z',
  snowflake: 'M12 2v20M2 12h20M5 5l14 14M19 5L5 19M8 2l4 3 4-3M8 22l4-3 4 3M2 8l3 4-3 4M22 8l-3 4 3 4',
  tv: 'M2 7h20v13H2zM7 2l5 5 5-5',
  heart: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z',
  diamond: 'M6 3h12l4 6-10 13L2 9z',
  droplet: 'M12 2.69l5.66 5.66a8 8 0 11-11.31 0L12 2.69z',
  leaf: 'M11 20A7 7 0 014 13c0-2 1-3.9 2-5.5C7 6 9 4.5 11 4c2 0 4-1 6-2-1 7-2 13-6 18zM2 22c2-2 4-4 4-7',
};

let nextThemePickerId = 0;

@Component({
  selector: 'app-theme-picker',
  standalone: true,
  imports: [],
  templateUrl: './theme-picker.component.html',
  styleUrls: ['./theme-picker.component.scss'],
})
export class ThemePickerComponent {
  private el = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);
  protected themeService = inject(ThemeService);

  protected isOpen = signal(false);
  protected popoverId = 'theme-picker-' + ++nextThemePickerId;

  // Ordered to match Online Colo's theme picker.
  protected readonly availableThemes: ThemeOption[] = [
    { id: 'vb-graphite',        name: 'VB Graphite',           icon: 'columns' },
    { id: 'vb-pearl',           name: 'VB Pearl',              icon: 'certificate' },
    // Narrowed to the two functional VB themes per UAT feedback. The
    // remaining options are retained for possible future use.
    // { id: 'vb-corporate',       name: 'VB Corporate (Light)',  icon: 'sun' },
    // { id: 'vb-corporate-dark',  name: 'VB Corporate (Dark)',   icon: 'moon' },
    // { id: 'slack',              name: 'Slack',                 icon: 'chat' },
    // { id: 'winter-chill',       name: 'Winter Chill',          icon: 'snowflake' },
    // { id: 'retro',              name: 'Retro',                 icon: 'tv' },
    // { id: 'valentine',          name: 'Valentine',             icon: 'heart' },
    // { id: 'luxury',             name: 'Luxury',                icon: 'diamond' },
    // { id: 'abyss',              name: 'Abyss',                 icon: 'droplet' },
    // { id: 'abyss-lime',         name: 'Abyss Lime',            icon: 'leaf' },
  ];

  constructor() {
    // If the persisted theme is no longer one of the curated options, fall
    // back to the default so the picker has a valid active selection.
    if (!this.availableThemes.some(t => t.id === this.themeService.currentTheme())) {
      this.themeService.setTheme('vb-graphite');
    }
  }

  iconPath(name: IconKey): string {
    return THEME_ICON_PATHS[name];
  }

  selectTheme(themeId: string): void {
    this.themeService.setTheme(themeId);
    this.cdr.detectChanges();
    const el = document.getElementById(this.popoverId) as HTMLElement & { hidePopover?: () => void };
    el?.hidePopover?.();
  }

  onPopoverToggle(event: Event): void {
    const newState = (event as ToggleEvent).newState;
    this.isOpen.set(newState === 'open');
    if (newState === 'open') {
      this.positionDropdown();
    }
  }

  private positionDropdown(): void {
    const btn = this.el.nativeElement.querySelector('.theme-picker-btn') as HTMLElement;
    const dropdown = document.getElementById(this.popoverId) as HTMLElement;
    if (!btn || !dropdown) return;
    const rect = btn.getBoundingClientRect();
    dropdown.style.position = 'fixed';
    dropdown.style.top = `${rect.bottom + 8}px`;
    dropdown.style.right = `${window.innerWidth - rect.right}px`;
    dropdown.style.left = 'auto';
  }
}
